import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { 
  ChefHat, 
  Shirt, 
  DoorOpen, 
  PanelTop,
  Droplets,
  Sun,
  Wind,
  Thermometer,
  Wallet,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
  RotateCcw,
  MessageCircle,
  Download,
  Phone,
  Star,
  Shield,
  Flame
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIProductRecommender = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    roomType: null,
    humidity: null,
    heatExposure: null,
    budget: null,
    style: null
  });
  const [recommendation, setRecommendation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const steps = [
    {
      id: 'roomType',
      question: 'Kahan ke liye product chahiye?',
      questionEn: 'What room are you looking for?',
      options: [
        { id: 'kitchen', label: 'Kitchen', labelHi: 'किचन', icon: ChefHat, desc: 'Modular kitchen cabinets & shutters' },
        { id: 'wardrobe', label: 'Wardrobe', labelHi: 'वार्डरोब', icon: Shirt, desc: 'Bedroom wardrobes & closets' },
        { id: 'door', label: 'Doors', labelHi: 'दरवाज़े', icon: DoorOpen, desc: 'Interior & exterior doors' },
        { id: 'wall', label: 'Wall Panels', labelHi: 'वॉल पैनल', icon: PanelTop, desc: 'Decorative wall cladding' }
      ]
    },
    {
      id: 'humidity',
      question: 'Room mein humidity/moisture level kaisa hai?',
      questionEn: 'What is the humidity level in the room?',
      options: [
        { id: 'high', label: 'High Humidity', labelHi: 'बहुत नमी', icon: Droplets, desc: 'Bathroom, coastal area, near sink' },
        { id: 'medium', label: 'Medium Humidity', labelHi: 'सामान्य नमी', icon: Wind, desc: 'Normal room, good ventilation' },
        { id: 'low', label: 'Low/Dry', labelHi: 'सूखा', icon: Sun, desc: 'AC room, dry climate' }
      ]
    },
    {
      id: 'heatExposure',
      question: 'Heat exposure kitna hai?',
      questionEn: 'How much heat exposure?',
      options: [
        { id: 'high', label: 'High Heat', labelHi: 'बहुत गर्मी', icon: Flame, desc: 'Near gas stove, direct sunlight' },
        { id: 'medium', label: 'Medium Heat', labelHi: 'सामान्य', icon: Thermometer, desc: 'Normal room temperature' },
        { id: 'low', label: 'Low/AC', labelHi: 'ठंडा/AC', icon: Wind, desc: 'Air-conditioned, cool area' }
      ]
    },
    {
      id: 'budget',
      question: 'Aapka budget kya hai?',
      questionEn: 'What is your budget range?',
      options: [
        { id: 'premium', label: 'Premium', labelHi: 'प्रीमियम', icon: Star, desc: 'Best quality, European finish' },
        { id: 'mid', label: 'Mid-Range', labelHi: 'मध्यम', icon: Shield, desc: 'Great quality, value for money' },
        { id: 'economy', label: 'Economy', labelHi: 'किफ़ायती', icon: Wallet, desc: 'Good quality, budget-friendly' }
      ]
    },
    {
      id: 'style',
      question: 'Aapko kaisa look chahiye?',
      questionEn: 'What style do you prefer?',
      options: [
        { id: 'glossy', label: 'High Gloss', labelHi: 'चमकदार', icon: Sparkles, desc: 'Mirror-like shine, modern look' },
        { id: 'matte', label: 'Matte Finish', labelHi: 'मैट फिनिश', icon: PanelTop, desc: 'Subtle elegance, no reflection' },
        { id: 'wood', label: 'Wood Texture', labelHi: 'लकड़ी जैसा', icon: DoorOpen, desc: 'Natural wood grain look' },
        { id: 'solid', label: 'Solid Colors', labelHi: 'सॉलिड कलर', icon: Star, desc: 'Bold, uniform colors' }
      ]
    }
  ];

  const handleAnswer = (stepId, answerId) => {
    setAnswers(prev => ({ ...prev, [stepId]: answerId }));
    
    if (currentStep < steps.length - 1) {
      setTimeout(() => setCurrentStep(prev => Math.min(prev + 1, steps.length - 1)), 300);
    } else {
      // Last step - generate recommendation
      generateRecommendation({ ...answers, [stepId]: answerId });
    }
  };

  const generateRecommendation = async (finalAnswers) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalAnswers)
      });

      if (response.ok) {
        const data = await response.json();
        setRecommendation(normalizeRecommendation(data, finalAnswers));
      } else {
        // Fallback to local recommendation logic
        setRecommendation(getLocalRecommendation(finalAnswers));
      }
    } catch (error) {
      console.error('Recommendation error:', error);
      // Fallback to local recommendation
      setRecommendation(getLocalRecommendation(finalAnswers));
    } finally {
      setIsLoading(false);
      setShowResult(true);
    }
  };

  const getLocalRecommendation = (answers) => {
    // Smart local recommendation logic based on answers
    const { roomType, humidity, heatExposure, budget, style } = answers;
    
    let primaryProduct = '';
    let productDesc = '';
    let features = [];
    let whyThisProduct = '';
    let alternateProduct = '';
    let guarantee = '10 years';

    // Kitchen recommendations
    if (roomType === 'kitchen') {
      if (budget === 'premium') {
        primaryProduct = '6H Acrylic Laminated Boards';
        productDesc = 'Premium acrylic surface with mirror-like finish, scratch-resistant, and heat-proof up to 150°C';
        features = [
          '6H surface hardness - won\'t scratch',
          'Heat resistant up to 150°C (perfect near gas stove)',
          '100% waterproof with PUR bonding',
          'Non-yellowing even after years of cooking',
          'Easy to clean, chemical resistant'
        ];
        whyThisProduct = 'Aapke premium kitchen ke liye perfect choice! Gas stove ke paas bhi safe, 15 saal tak naya jaisa dikhega.';
        alternateProduct = 'CNC Designer Doors for matching aesthetic';
      } else if (budget === 'mid') {
        primaryProduct = 'Mica Laminated Boards with PUR';
        productDesc = 'High-quality mica laminate with factory PUR bonding for durability';
        features = [
          'Factory-laminated with CriLam PUR-Bond™ technology',
          'Heat resistant for kitchen use',
          'Waterproof bonding',
          'Wide range of colors and textures',
          'Value for money with premium performance'
        ];
        whyThisProduct = 'Budget-friendly lekin quality se compromise nahi! PUR bonding se edge lifting ki tension nahi.';
        alternateProduct = '6H Acrylic for high-visibility areas';
      } else {
        primaryProduct = 'Mica Laminated Boards';
        productDesc = 'Cost-effective laminated boards with good durability';
        features = [
          'Factory PUR lamination',
          'Good heat resistance',
          'Water resistant',
          'Budget-friendly option',
          'Variety of finishes available'
        ];
        whyThisProduct = 'Kam budget mein bhi CriLam quality! Factory lamination ka fayda milega.';
        alternateProduct = 'Consider upgrading to mid-range for better longevity';
      }
    }
    
    // Wardrobe recommendations
    else if (roomType === 'wardrobe') {
      if (humidity === 'high') {
        primaryProduct = '6H Acrylic Laminated Boards';
        productDesc = 'Moisture-proof acrylic boards perfect for humid bedrooms and coastal areas';
        features = [
          '100% waterproof PUR bonding',
          'No edge lifting in humidity',
          'Salt-air resistant (coastal areas)',
          'Scratch-resistant surface',
          'Premium European finish'
        ];
        whyThisProduct = 'Coastal area ya humid room mein perfect! PUR bonding se lifetime guarantee.';
        alternateProduct = 'Matte finish for subtle elegance';
      } else if (style === 'wood') {
        primaryProduct = 'Veneer Laminated Boards (Nail-Free)';
        productDesc = 'Natural wood veneer with seamless nail-free lamination';
        features = [
          'Real wood veneer texture',
          'Nail-free technology for seamless look',
          'PUR bonded for durability',
          'Natural grain patterns',
          'Premium furniture finish'
        ];
        whyThisProduct = 'Natural wood ka look chahiye toh perfect choice! Nail-free technology se seamless finish.';
        alternateProduct = 'Wood-texture acrylic for easier maintenance';
      } else {
        primaryProduct = budget === 'premium' ? '6H Acrylic Laminated Boards' : 'Mica Laminated Boards';
        productDesc = budget === 'premium' 
          ? 'Premium acrylic finish for luxury wardrobes'
          : 'Quality mica finish with factory PUR bonding';
        features = [
          'Factory PUR lamination',
          'Moisture resistant',
          'Wide color range',
          style === 'glossy' ? 'High-gloss mirror finish' : 'Elegant matte finish',
          'Long-lasting durability'
        ];
        whyThisProduct = 'Aapki style preference ke according perfect match!';
        alternateProduct = 'Consider matching doors for complete look';
      }
    }
    
    // Door recommendations
    else if (roomType === 'door') {
      if (humidity === 'high') {
        primaryProduct = 'CNC Designer Doors with PUR Lamination';
        productDesc = 'Waterproof CNC doors perfect for bathrooms and wet areas';
        features = [
          '100% waterproof construction',
          'CNC precision cutting',
          'PUR laminated surfaces',
          'No swelling or warping',
          'Designer 3D patterns available'
        ];
        whyThisProduct = 'Bathroom ke liye perfect! 100% waterproof, swell nahi hoga, 10 saal guarantee.';
        alternateProduct = 'Mica doors for budget option';
      } else if (heatExposure === 'high') {
        primaryProduct = 'CNC Designer Doors - Heat Resistant';
        productDesc = 'UV and heat stable doors for sun-facing areas';
        features = [
          'UV stable - no fading',
          'Heat resistant lamination',
          'Designer CNC patterns',
          'Thermal stability',
          'Weather resistant'
        ];
        whyThisProduct = 'Balcony ya sun-facing door ke liye ideal! UV se fade nahi hoga.';
        alternateProduct = 'Veneer doors for traditional look';
      } else {
        primaryProduct = style === 'wood' ? 'Veneer Doors (Nail-Free)' : 'CNC Designer Doors';
        productDesc = style === 'wood' 
          ? 'Natural veneer finish with premium nail-free technology'
          : 'Modern CNC-cut doors with contemporary designs';
        features = [
          'Premium finish quality',
          'PUR lamination technology',
          'Wide design range',
          'Durable construction',
          '10-year guarantee'
        ];
        whyThisProduct = 'Aapki style choice ke perfect! Interior complete look ke liye best.';
        alternateProduct = 'Matching wall panels for coordinated design';
      }
    }
    
    // Wall Panel recommendations
    else {
      if (heatExposure === 'high' || humidity === 'high') {
        primaryProduct = '6H Acrylic Wall Panels';
        productDesc = 'Premium wall panels with weather and moisture resistance';
        features = [
          'Weather resistant for any climate',
          'Thermal stability',
          '6H scratch resistance',
          'Easy cleaning',
          'Premium finish'
        ];
        whyThisProduct = 'Commercial ya challenging environment ke liye perfect!';
        alternateProduct = 'Decorative panels for accent walls';
      } else {
        primaryProduct = 'Decorative Wall Panels';
        productDesc = 'Designer wall panels with acoustic properties';
        features = [
          'Acoustic insulation',
          'Designer patterns',
          'PUR lamination',
          'Easy installation',
          'Low maintenance'
        ];
        whyThisProduct = 'Living room ya office ke liye elegant choice!';
        alternateProduct = '6H Acrylic for premium finish';
      }
    }

    return {
      success: true,
      primaryProduct,
      productDesc,
      features,
      whyThisProduct,
      alternateProduct,
      guarantee,
      purBenefit: 'All CriLam products come with PUR (Polyurethane Reactive) bonding - 100% waterproof, heat resistant up to 150°C, zero bubbles, edges never lift. Made at Kanpur\'s first CriLam PUR-Bond™ lamination facility!'
    };
  };

  const normalizeRecommendation = (data, finalAnswers) => {
    const fallback = getLocalRecommendation(finalAnswers);
    const merged = { ...fallback };
    Object.keys(fallback).forEach(key => {
      if (data && data[key] !== undefined && data[key] !== null && data[key] !== '') {
        merged[key] = data[key];
      }
    });
    if (!Array.isArray(merged.features) || merged.features.length === 0) {
      merged.features = fallback.features;
    }
    return merged;
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({
      roomType: null,
      humidity: null,
      heatExposure: null,
      budget: null,
      style: null
    });
    setRecommendation(null);
    setShowResult(false);
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const answerLabel = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    return step?.options.find(o => o.id === answers[stepId])?.label || '-';
  };

  const quotePrefill = recommendation ? {
    product: recommendation.primaryProduct,
    subject: `Quote Request: ${recommendation.primaryProduct}`,
    message: `Hi CriLam Team,\n\nYour AI Product Recommender suggested "${recommendation.primaryProduct}" for my requirement:\n- Room: ${answerLabel('roomType')}\n- Humidity: ${answerLabel('humidity')}\n- Heat Exposure: ${answerLabel('heatExposure')}\n- Budget: ${answerLabel('budget')}\n- Style: ${answerLabel('style')}\n\nPlease share a detailed quote for this.`
  } : null;

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="ai-recommender-page">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <Sparkles className="h-4 w-4" />
              AI PRODUCT RECOMMENDER
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Find Your Perfect Product
            </h1>
            <p className="text-xl text-[#D4AF37] font-medium mb-4">
              Answer 5 Simple Questions, Get Personalized Recommendations
            </p>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Humara AI aapki zaroorat samajh kar best CriLam product suggest karega
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            
            {/* Progress Bar */}
            {!showResult && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#004534]">
                    Question {currentStep + 1} of {steps.length}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Math.round(progress)}% complete
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-[#004534]"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            )}

            {/* Quiz Steps */}
            {!showResult && !isLoading && currentStepData && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl shadow-xl p-8"
                >
                  {/* Question */}
                  <div className="text-center mb-8">
                    <h2 className="text-2xl lg:text-3xl font-bold text-[#004534] mb-2 font-['Outfit']">
                      {currentStepData?.question}
                    </h2>
                    <p className="text-gray-500">{currentStepData?.questionEn}</p>
                  </div>

                  {/* Options */}
                  <div className="grid gap-4">
                    {(currentStepData?.options || []).map((option) => (
                      <motion.button
                        key={option.id}
                        onClick={() => handleAnswer(currentStepData.id, option.id)}
                        className={`w-full p-5 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                          answers[currentStepData.id] === option.id
                            ? 'border-[#004534] bg-[#004534]/5'
                            : 'border-gray-200 hover:border-[#004534]/50 hover:bg-gray-50'
                        }`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        data-testid={`option-${option.id}`}
                      >
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                          answers[currentStepData.id] === option.id
                            ? 'bg-[#004534] text-white'
                            : 'bg-gray-100 text-[#004534]'
                        }`}>
                          <option.icon className="h-7 w-7" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#004534]">{option.label}</span>
                            <span className="text-sm text-gray-400">({option.labelHi})</span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{option.desc}</p>
                        </div>
                        {answers[currentStepData.id] === option.id && (
                          <CheckCircle className="h-6 w-6 text-[#004534]" />
                        )}
                      </motion.button>
                    ))}
                  </div>

                  {/* Navigation */}
                  {currentStep > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-gray-500 hover:text-[#004534] transition-colors"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Previous Question
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}

            {/* Loading State */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-2xl shadow-xl p-12 text-center"
              >
                <Loader2 className="h-16 w-16 text-[#004534] animate-spin mx-auto mb-6" />
                <h3 className="text-xl font-bold text-[#004534] mb-2">
                  Analyzing Your Requirements...
                </h3>
                <p className="text-gray-500">
                  Finding the perfect CriLam product for you
                </p>
              </motion.div>
            )}

            {/* Result */}
            {showResult && recommendation && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Primary Recommendation */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
                  <div className="bg-[#004534] text-white p-6">
                    <div className="flex items-center gap-2 text-[#D4AF37] text-sm font-bold mb-2">
                      <Star className="h-4 w-4 fill-current" />
                      RECOMMENDED FOR YOU
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold font-['Outfit']">
                      {recommendation.primaryProduct}
                    </h2>
                    <p className="text-white/80 mt-2">{recommendation.productDesc}</p>
                  </div>

                  <div className="p-6">
                    {/* Why This Product */}
                    <div className="bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] p-4 rounded-r-lg mb-6">
                      <p className="text-[#004534] font-medium">
                        💡 {recommendation.whyThisProduct}
                      </p>
                    </div>

                    {/* Features */}
                    <h3 className="font-bold text-[#004534] mb-4">Key Features:</h3>
                    <ul className="space-y-3 mb-6">
                      {(Array.isArray(recommendation.features) ? recommendation.features : []).map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Price & Guarantee */}
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500 mb-1">Technology</p>
                        <p className="text-xl font-bold text-[#004534]">CriLam PUR-Bond™</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500 mb-1">Guarantee</p>
                        <p className="text-xl font-bold text-[#004534]">{recommendation.guarantee}</p>
                      </div>
                    </div>

                    {/* PUR Benefit */}
                    <div className="p-4 bg-[#004534] text-white rounded-xl">
                      <div className="flex items-start gap-3">
                        <Shield className="h-6 w-6 text-[#D4AF37] flex-shrink-0" />
                        <div>
                          <p className="font-bold text-[#D4AF37] mb-1">CriLam PUR Advantage</p>
                          <p className="text-sm text-white/80">{recommendation.purBenefit}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alternate Product */}
                <div className="bg-gray-100 rounded-xl p-6 mb-6">
                  <p className="text-sm text-gray-500 mb-1">Also Consider:</p>
                  <p className="font-medium text-[#004534]">{recommendation.alternateProduct}</p>
                </div>

                {/* Action Buttons */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <a
                    href={quotePrefill ? `https://wa.me/918298297475?text=${encodeURIComponent(quotePrefill.message)}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <button className="w-full py-4 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#128C7E] transition-colors flex items-center justify-center gap-2" data-testid="whatsapp-quote-btn">
                      <MessageCircle className="h-5 w-5" />
                      WhatsApp Quote
                    </button>
                  </a>
                  <Link to="/contact" state={{ prefill: quotePrefill }}>
                    <button className="w-full py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-xl hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2" data-testid="get-quote-btn">
                      <Phone className="h-5 w-5" />
                      Get Quote
                    </button>
                  </Link>
                  <Link to="/ai-visualizer">
                    <button className="w-full py-4 border-2 border-[#004534] text-[#004534] font-bold rounded-xl hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      Visualize in Your Room
                    </button>
                  </Link>
                </div>

                {/* Restart */}
                <div className="text-center mt-8">
                  <button
                    onClick={resetQuiz}
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-[#004534] transition-colors"
                    data-testid="start-over-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Start Over with Different Requirements
                  </button>
                </div>
              </motion.div>
            )}

          </div>
        </div>
      </section>

      {/* Help Section */}
      {!showResult && (
        <section className="py-12 bg-white border-t">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-gray-500 mb-4">
                Need help deciding? Talk to our experts
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="tel:8298297475" className="inline-flex items-center gap-2 text-[#004534] hover:text-[#D4AF37] transition-colors">
                  <Phone className="h-5 w-5" />
                  8298297475
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 text-[#004534] hover:text-[#D4AF37] transition-colors">
                  <MessageCircle className="h-5 w-5" />
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default function AIProductRecommenderPage() {
  return (
    <ErrorBoundary>
      <AIProductRecommender />
    </ErrorBoundary>
  );
}
